import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { m2mCleaning } from "@/endpoints/m2m-cleaning"
import { CleaningType } from "@/domain/cleaning"

export interface CleaningExtraInfo {
    sumycaListingId: string
}

export interface CompanySubContractDocument {
    id: string
    companyApprovalRequestId: string
    executionDate: string
    unitPrice: number
    tourName: string
    placementName: string
    orderingCompanyName: string
    receivingCompanyName: string
    serviceDate: string
    paymentDueDate: string
    inspectionCompletionDate: string
    workCompletionDate: string
    paidSubcontractAmount: number
    paymentDate: string
    paymentMethod: string
    createdAt: number
    updatedAt: number
}

interface CompanyApprovalRequestItem {
    id: string
    cleaningId: string
    cleaningDate: string
    scheduleStart: number | null
    scheduleEnd: number | null
    photoTourId: string | null
    tourName: string | null
    placement: "listing" | "common_area"
    placementName: string
    cleaningType: CleaningType
}

export interface CompanyApprovalRequestsResponse {
    items: CompanyApprovalRequestItem[]
    totalCount: number
    page: number
    pageSize: number
}

export const useCompanyApprovalRequests = (
    token: string,
    page = 1,
    pageSize = 20
) => {
    const endpoint = m2mCleaning.managerV4.listPendingCompanyApprovalRequests
    const url = `${endpoint()}?page=${page}&pageSize=${pageSize}` as const

    return useAuthBearerFetch<CompanyApprovalRequestsResponse>(token, url, {
        method: endpoint.method
    })
}

export const useCompanyApprovalRequestsCount = (token: string) => {
    const endpoint = m2mCleaning.managerV4.countPendingCompanyApprovalRequests

    return useAuthBearerFetch<{ count: number }>(token, endpoint(), {
        method: endpoint.method
    })
}

export const requestApproveCompanyRequest = async (
    token: string,
    companyApprovalRequestId: string
) => {
    const endpoint = m2mCleaning.managerV4.approveCompanyRequest
    return bearerFetcher<void>(token, endpoint({ companyApprovalRequestId }), {
        method: endpoint.method
    })
}

export const requestRejectCompanyRequest = async (
    token: string,
    companyApprovalRequestId: string
) => {
    const endpoint = m2mCleaning.managerV4.rejectCompanyRequest
    return bearerFetcher<void>(token, endpoint({ companyApprovalRequestId }), {
        method: endpoint.method
    })
}

export const requestBulkApproveCompanyRequests = async (token: string) => {
    const endpoint = m2mCleaning.managerV4.bulkApproveCompanyRequests
    return bearerFetcher<void>(token, endpoint(), {
        method: endpoint.method
    })
}

export const useCleaningExtraInfo = (
    token: string,
    cleaningId: string | null,
    companyApprovalRequestId: string | undefined
) => {
    const endpoint = m2mCleaning.cleanerV4.getCleaningExtraInfo
    const url =
        cleaningId && companyApprovalRequestId
            ? `${endpoint()}?cleaningId=${cleaningId}&companyApprovalRequestId=${companyApprovalRequestId}`
            : null

    return useAuthBearerFetch<CleaningExtraInfo>(token, url, {
        method: endpoint.method
    })
}

export const useCompanySubContractDocument = (
    token: string,
    companyApprovalRequestId: string
) => {
    const endpoint =
        m2mCleaning.managerV4
            .getCompanySubContractDocumentByCompanyApprovalRequestId

    return useAuthBearerFetch<CompanySubContractDocument>(
        token,
        endpoint({ companyApprovalRequestId }),
        {
            method: endpoint.method
        }
    )
}

export const requestDownloadCompanySubContractDocumentPdf = async (
    token: string,
    companyApprovalRequestId: string
) => {
    const endpoint =
        m2mCleaning.managerV4
            .createCompanySubContractDocumentPdfByCompanyApprovalRequestId
    const url = endpoint({ companyApprovalRequestId })

    // eslint-disable-next-line no-restricted-globals -- BlobレスポンスのためfetcherはJSONレスポンスを前提としており直接fetchを使用
    const response = await fetch(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`
        }
    })

    if (!response.ok) {
        throw new Error("PDFのダウンロードに失敗しました")
    }

    return response.blob()
}
